import {
	convertToUTCDate,
	DatepickerTranslationService,
	dateToString,
	generateMonth,
	groupByCalendarWeek,
	WeekDays,
} from '@agilox/common';
import {
	AfterViewInit,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	output,
	signal,
	viewChildren,
} from '@angular/core';
import { CalendarDate, CalendarWeek, CalendarWeekOutput } from '@agilox/ui-common';

@Component({
	selector: 'ui-datepicker-calendar',
	templateUrl: './datepicker-calendar.component.html',
	standalone: false,
})
export class DatepickerCalendarComponent implements AfterViewInit {
	private translationService = inject(DatepickerTranslationService);

	minDate = input<Date | undefined>();
	maxDate = input<Date | undefined>();
	disabled = input.required<boolean>();
	currentSelection = input<string | null>(null);
	syncedDate = input<Date | undefined>();
	excludedDates = input<Date[]>([]);
	currentlyDisplayedMonth = input<Date | null>(null);

	dateSelectionChanged = output<string>();
	monthChange = output<Date>();
	calendarWeekClicked = output<CalendarWeekOutput>();

	calendarDays = viewChildren<ElementRef>('calendarDay');

	translatedCalendarWeek = computed(() => {
		return this.translationService.translate('datepicker.calendar_week');
	});

	days = signal<CalendarDate[]>([]);
	calendarWeeks = signal<CalendarWeek[]>([]);

	weekDays: string[] = Object.values(WeekDays).filter((v) => typeof v !== 'number');

	translateWeekday(weekDay: string): string {
		return this.translationService.translate(`datepicker.weekday.${weekDay}`);
	}

	constructor() {
		effect(() => {
			if (this.currentlyDisplayedMonth()) {
				this.setDays(this.currentlyDisplayedMonth()!);
			}
		});
	}

	ngAfterViewInit(): void {
		this.focusLastValidCalendarDay();
	}

	private focusLastValidCalendarDay(): void {
		const calendarDays = this.calendarDays();
		if (calendarDays.length > 0) {
			const notSelectable = 'cursor-not-allowed!';
			const validCalendarDays = calendarDays.filter(
				(day) => !Array.from(day?.nativeElement.classList).includes(notSelectable)
			);
			if (validCalendarDays.length > 0) {
				validCalendarDays[validCalendarDays.length - 1]?.nativeElement.focus();
			}
		}
	}

	private setDays(month: Date): void {
		this.days.set(
			generateMonth(
				month.getFullYear(),
				month.getMonth() + 1,
				this.disabled(),
				this.minDate(),
				this.maxDate()
			)
		);
		const excludedDays = this.getExcludedDays(this.days());

		this.calendarWeeks.set(groupByCalendarWeek(excludedDays));
	}

	isDateSelected(day: CalendarDate): boolean {
		if (day.date === this.currentSelection()) {
			return true;
		}
		const synced = this.syncedDate();
		if (synced) {
			function resetTime(date: Date | string): Date {
				const tempDate = new Date(date);
				return new Date(tempDate.setHours(0, 0, 0, 0));
			}

			const syncedDateTime = resetTime(synced).getTime();
			const dayTimestamp = resetTime(convertToUTCDate(day.date)).getTime();

			return dayTimestamp === syncedDateTime;
		}
		return false;
	}

	isInRange(day: CalendarDate): boolean {
		const selection = this.currentSelection();
		const syncedDate = this.syncedDate();

		if (!selection || day.disabled || !syncedDate) {
			return false;
		}

		const dayTimestamp = this.normalizeToMidnight(new Date(day.date)).getTime();
		const syncedDateTimestamp = this.normalizeToMidnight(new Date(syncedDate)).getTime();
		const selectedDayTimestamp = this.normalizeToMidnight(new Date(selection)).getTime();

		const startTime = Math.min(syncedDateTimestamp, selectedDayTimestamp);
		const endTime = Math.max(syncedDateTimestamp, selectedDayTimestamp);

		return dayTimestamp >= startTime && dayTimestamp <= endTime;
	}

	private normalizeToMidnight(date: Date): Date {
		const normalizedDate = new Date(date);
		normalizedDate.setHours(0, 0, 0, 0); // Set time to midnight
		return normalizedDate;
	}

	selectDay(day: CalendarDate) {
		if (day.disabled || this.disabled()) {
			return;
		}
		if (day.notInActiveMonth) {
			this.monthChange.emit(convertToUTCDate(day.date));
			return;
		}
		this.dateSelectionChanged.emit(day.date);
	}

	getExcludedDays(days: CalendarDate[]) {
		const selection = this.currentSelection();
		const syncedDate = this.syncedDate();

		if (!selection || !syncedDate || !this.excludedDates().length) {
			return days;
		}
		return days.map((d) => {
			const isExcluded = this.excludedDates().some((date) => dateToString(date) === d.date);
			return {
				...d,
				excluded: isExcluded,
			};
		});
	}

	public onCalendarWeekClicked(week: CalendarWeek): void {
		// only select the days that are not disabled
		// if all days are disabled we do nothing
		const days = week.days.filter((day) => !day.disabled);
		if (!days.length) {
			return;
		}

		this.calendarWeekClicked.emit({
			start: days[0].date,
			end: days[days.length - 1].date,
		});
	}
}
